import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	getAwsCredentials,
	getCognitoAuthSession,
} from 'src/authentication/cognito';
import {
	startCredentialRefreshManager,
	stopCredentialRefreshManager,
} from 'src/authentication/credentialRefreshManager';

type AuthContextType = {
	userName?: string;
	authContextDetails?: object;
	userDetailsLoading?: boolean;
};

interface RunDateProviderProps {
	children: React.ReactNode;
}

export const AuthContext = createContext<AuthContextType>({
	userDetailsLoading: false,
});

export const useAuthContext = () => {
	return useContext(AuthContext);
};

const AuthContextProvider = ({ children }: RunDateProviderProps) => {
	const [userName, setUserName] = useState();
	const [loading, setLoading] = useState(false);
	const [authContextDetails, setAuthContextDetails] = useState();

	const getCognitoSession = () => {
		setLoading(true);
		getCognitoAuthSession()
			.then(async (response) => {
				setAuthContextDetails(response);
				setUserName(response.accessToken.payload.username.split('_')[1]);

				await getAwsCredentials();
				startCredentialRefreshManager();
			})
			.catch((error) => {
				console.log('Error while getting cognito session ', error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (!userName) {
			getCognitoSession();
		}
	}, [userName]);

	useEffect(() => {
		return () => {
			stopCredentialRefreshManager();
		};
	}, []);

	const value = useMemo<AuthContextType>(
		() => ({
			userName,
			authContextDetails,
			userDetailsLoading: loading,
		}),
		[userName, authContextDetails, loading]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
